import { useGlobalNotifier, useMessage } from "@vrf/fr-vrf-core-react";
import {
  FrVrfButton,
  FrVrfCheckboxField,
  FrVrfCheckboxesField,
  FrVrfFooter,
  FrVrfFormPanel,
  FrVrfMultipleLineInputField,
  FrVrfPanel,
  FrVrfSingleLineDisplayField,
  FrVrfSingleLineInputField,
} from "@vrf/fr-vrf-web-components-react";
import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { ApplicationMessageScreen } from "../../../messages/screen-message";
import { usePageNavigation } from "../../../pages/use-page-navigation";
import {
  UpdateComponentRequest,
  useUpdateComponent,
} from "../../../services/component";
import {
  CreateDynamicEdhProtocol,
  createDynamicEdhProtocol,
} from "../../../util/edh-component-logic";
import {
  InDefCommonFlagType,
  IoType,
} from "../../../util/general-code/enums/general-codes";
import { createModal } from "../../modal";
import { isChecked } from "./ComponentEditDialogConstant";
import { useComponentEditDialogState } from "./useComponentEditDialogState";
import { componentEditDialogInitialState } from "./ComponentEditDialogInitialState";
export const ComponentEditDialogContent: React.FC = () => {
  const { showConfirmationDialog, showToast, errorMessages, resetUserError } =
    useGlobalNotifier();
  const { messageForScreen } = useMessage();

  const [dialogState, setDialogState] = useComponentEditDialogState();
  const { component, formEditData, dataList } = dialogState;

  const [updateComponentResponse, sendUpdateComponentRequest] =
    useUpdateComponent();
  const search = useLocation().search;
  const queryParameter = new URLSearchParams(search);
  const { moveToModalPage, closeModalPage, isModalPage } = usePageNavigation();
  const params = {
    dipByRgn: queryParameter.get("dipByRgn") ?? "",
    rootCpntId: queryParameter.get("rootCpntId") ?? "",
    cpntId: queryParameter.get("cpntId") ?? "",
    godFlg: queryParameter.get("godFlg") ?? "",
  };
  const onInitialDisplayed = () => {
    moveToModalPage();
    resetUserError();
  };

  const closeModal = () => {
    closeModalPage();
    setDialogState((prevState) => ({
      ...prevState,
      isShow: false,
      isEdit: false,
      formValidation: {
        cpntNameWtngChng: true,
        cpntDesc: true,
        gpgEncryptnFlg: true,
        useAssmdRoleFlg: true,
        useEdhCnvtrFlg: true,
        hubFlg: true,
        edhPrtclList: true,
        ifStrtMthdList: true,
        inboundNotificatorList: true,
        outboundNotificatorList: true,
        ediUsgFlg: true,
      },
    }));
  };

  const onUpdateComponent = () => {
    if (!!updateComponentResponse) {
      showToast({
        design: "completion",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00044),
      });
      closeModalPage();
      setDialogState((_prevState) => ({
        ...componentEditDialogInitialState,
        isUpdated: true,
      }));
    }
  };

  const onUpdateDynamicCheckboxes = () => {
    const createDynamicEdhProtocolProps: CreateDynamicEdhProtocol = {
      edhProtocolCombinationList: dialogState.edhProtocolCombination,
      edhProtocolList: dataList.edhProtcolList,
      edhProtocolCheckList: formEditData.edhPrtclList,
    };

    const { dynamicEdhProtocolList, edhProtocolCheckList } =
      createDynamicEdhProtocol(createDynamicEdhProtocolProps);

    const isChangeValue =
      JSON.stringify(formEditData.edhPrtclList) !==
      JSON.stringify(edhProtocolCheckList);

    setDialogState((prevState) => ({
      ...prevState,
      formEditData: {
        ...prevState.formEditData,
        edhPrtclList: isChangeValue
          ? edhProtocolCheckList
          : formEditData.edhPrtclList,
      },
      dataList: {
        ...prevState.dataList,
        dynamicEdhProtocolList,
      },
    }));
  };

  useEffect(onInitialDisplayed, []);
  useEffect(onUpdateComponent, [updateComponentResponse]);
  useEffect(onUpdateDynamicCheckboxes, [
    dialogState.edhProtocolCombination,
    formEditData.edhPrtclList,
  ]);

  const onClickCancel = () => {
    if (dialogState.isEdit) {
      showConfirmationDialog({
        title: "",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00033),
        type: "confirm",
        onOkButtonClicked: closeModal,
      });
      return;
    }
    closeModal();
  };

  const onChangeValueHandle = (name: string) => (event: CustomEvent) => {
    const value = event.detail.value;
    const isValid = event.detail.isValid;
    setDialogState((prevState) => ({
      ...prevState,
      isEdit: true,
      component: {
        ...prevState.component,
        [name]: value,
      },
      formValidation: {
        ...prevState.formValidation,
        [name]: isValid,
      },
    }));
  };

  const onChangeCheckboxHandle = (name: string) => (event: CustomEvent) => {
    const value = event.detail.value;
    const isValid = event.detail.isValid;
    setDialogState((prevState) => ({
      ...prevState,
      isEdit: true,
      component: {
        ...prevState.component,
        [name]: value ? InDefCommonFlagType.YES : InDefCommonFlagType.NO,
      },
      formValidation: {
        ...prevState.formValidation,
        [name]: isValid,
      },
    }));
  };
  const onChangeCheckboxesHandle = (name: string) => (event: CustomEvent) => {
    const value: string[] = event.detail.value;
    const isValid = event.detail.isValid;
    setDialogState((prevState) => ({
      ...prevState,
      isEdit: true,
      formEditData: {
        ...prevState.formEditData,
        [name]: value,
      },
      formValidation: {
        ...prevState.formValidation,
        [name]: isValid,
      },
    }));
  };

  /**
   * Footer
   */
  const onClickSaveButton = () => {
    showConfirmationDialog({
      title: "",
      message: messageForScreen(ApplicationMessageScreen.APP_SCN_00045),
      type: "confirm",
      onOkButtonClicked: () => {
        resetUserError();
        const edhPrtclList: UpdateComponentRequest["edhPrtclList"] =
          formEditData.edhPrtclList.map((e) => {
            const value = e.split("/");
            return { inOutTypeCd: value[0], edhPrtclTypeCd: value[1] };
          });
        const ifStrtMthdList: UpdateComponentRequest["ifStrtMthdList"] =
          formEditData.ifStrtMthdList.map((e) => ({ ifStrtMthdTypeCd: e }));
        const inboundEdhNotificatorList =
          formEditData.inboundNotificatorList.map((e) => ({
            inOutTypeCd: IoType.INBOUND,
            edhNtfctnTypeCd: e,
          }));
        const outboundEdhNotificatorList =
          formEditData.outboundNotificatorList.map((e) => ({
            inOutTypeCd: IoType.OUTBOUND,
            edhNtfctnTypeCd: e,
          }));
        const edhNtfctnTypeList: UpdateComponentRequest["edhNtfctnTypeList"] = [
          ...inboundEdhNotificatorList,
          ...outboundEdhNotificatorList,
        ];

        const updateRequestDate: UpdateComponentRequest = {
          cpntId: component.cpntId,
          godFlg: params.godFlg,
          dipByRgn: params.dipByRgn,
          cpntNameWtngChng: component.cpntNameWtngChng,
          cpntDesc: component.cpntDesc,
          rootCpntId: params.rootCpntId,
          gpgEncryptnFlg: component.gpgEncryptnFlg,
          useAssmdRoleFlg: component.useAssmdRoleFlg,
          ediUsgFlg: component.ediUsgFlg,
          useEdhCnvtrFlg: component.useEdhCnvtrFlg,
          hubFlg: component.hubFlg,
          updateDateTime: component.updateDateTimeMCpnt,
          edhPrtclList: edhPrtclList,
          ifStrtMthdList: ifStrtMthdList,
          edhNtfctnTypeList: edhNtfctnTypeList,
        };
        sendUpdateComponentRequest(updateRequestDate);
      },
    });
  };

  return createModal(
    "Component Edit Dialog",
    <div>
      <div
        style={{
          paddingTop: "10px",
          paddingBottom: "30px",
          marginLeft: "30px",
        }}
      >
        {/* Component */}
        <FrVrfPanel panelTitle="Component">
          <FrVrfFormPanel columns={2}>
            <FrVrfFormPanel>
              <FrVrfSingleLineDisplayField
                label="Component ID"
                firstValue={component.cpntId}
              />
              <FrVrfSingleLineDisplayField
                label="Component Name"
                firstValue={component.cpntName}
              />
              <FrVrfSingleLineInputField
                labelContent="Component Name (Waiting to be changed)"
                content={component.cpntNameWtngChng}
                onInputValueChanged={onChangeValueHandle("cpntNameWtngChng")}
                errorMessage={errorMessages(
                  dialogState.fieldPath.cpntNameWtngChng,
                  isModalPage(),
                )}
                constraint="Size(value, {min: 0, max: 256})"
              />
              <FrVrfMultipleLineInputField
                tabindex="1"
                labelContent="Description"
                resize="both"
                content={component.cpntDesc}
                onInputValueChanged={onChangeValueHandle("cpntDesc")}
                errorMessage={errorMessages(
                  dialogState.fieldPath.cpntDesc,
                  isModalPage(),
                )}
                constraint="Size(value, {min: 0, max: 2000})"
              />
              <FrVrfCheckboxField
                labelContent="GPG Encryption"
                checked={isChecked(component.gpgEncryptnFlg)}
                onCheckedChanged={onChangeCheckboxHandle("gpgEncryptnFlg")}
                errorMessage={errorMessages(
                  dialogState.fieldPath.gpgEncryptnFlg,
                  isModalPage(),
                )}
              />
            </FrVrfFormPanel>
            <FrVrfFormPanel>
              <FrVrfCheckboxField
                labelContent="Use Assumed Role"
                checked={isChecked(component.useAssmdRoleFlg)}
                onCheckedChanged={onChangeCheckboxHandle("useAssmdRoleFlg")}
                errorMessage={errorMessages(
                  dialogState.fieldPath.useAssmdRoleFlg,
                )}
              />
              <FrVrfCheckboxField
                labelContent="Use EDH Converter"
                checked={isChecked(component.useEdhCnvtrFlg)}
                onCheckedChanged={onChangeCheckboxHandle("useEdhCnvtrFlg")}
                errorMessage={errorMessages(
                  dialogState.fieldPath.useEdhCnvtrFlg,
                  isModalPage(),
                )}
              />
              <FrVrfCheckboxField
                labelContent="HUB Flag"
                checked={isChecked(component.hubFlg)}
                onCheckedChanged={onChangeCheckboxHandle("hubFlg")}
                errorMessage={errorMessages(
                  dialogState.fieldPath.hubFlg,
                  isModalPage(),
                )}
              />
              <FrVrfCheckboxesField
                labelContent="EDH Protocol"
                options={dataList.dynamicEdhProtocolList}
                checkList={formEditData.edhPrtclList}
                onCheckboxesChanged={onChangeCheckboxesHandle("edhPrtclList")}
                errorMessage={errorMessages(
                  dialogState.fieldPath.edhPrtclList,
                  isModalPage(),
                )}
              />
              <FrVrfCheckboxesField
                labelContent="IF Start Method"
                options={dataList.startMethod}
                checkList={formEditData.ifStrtMthdList}
                onCheckboxesChanged={onChangeCheckboxesHandle("ifStrtMthdList")}
                errorMessage={errorMessages(
                  dialogState.fieldPath.ifStrtMthdList,
                  isModalPage(),
                )}
              />
              <FrVrfCheckboxesField
                labelContent="Inbound Notificator"
                options={dataList.inboundNotificator}
                checkList={formEditData.inboundNotificatorList}
                onCheckboxesChanged={onChangeCheckboxesHandle(
                  "inboundNotificatorList",
                )}
                errorMessage={errorMessages(
                  dialogState.fieldPath.edhNtfctnTypeList,
                )}
              />
              <FrVrfCheckboxesField
                labelContent="Outbound Notificator"
                options={dataList.outboundNotificator}
                checkList={formEditData.outboundNotificatorList}
                onCheckboxesChanged={onChangeCheckboxesHandle(
                  "outboundNotificatorList",
                )}
                errorMessage={errorMessages(
                  dialogState.fieldPath.edhNtfctnTypeList,
                )}
              />
              <FrVrfCheckboxField
                labelContent="EDI Usage"
                checked={isChecked(component.ediUsgFlg)}
                onCheckedChanged={onChangeCheckboxHandle("ediUsgFlg")}
                errorMessage={errorMessages(
                  dialogState.fieldPath.ediUsgFlg,
                  isModalPage(),
                )}
              />
            </FrVrfFormPanel>
          </FrVrfFormPanel>
        </FrVrfPanel>
        {/* Footer */}
        <FrVrfFooter>
          <FrVrfButton
            content="Cancel"
            theme="secondary"
            size="large"
            onClicked={onClickCancel}
          />
          <FrVrfButton
            content="Save"
            theme="primary"
            size="large"
            onClicked={onClickSaveButton}
            authActId="component_edit_dialog.button.save"
            disabled={Object.values(dialogState.formValidation).some(
              (isValid) => isValid === false,
            )}
          />
        </FrVrfFooter>
      </div>
    </div>,
    onClickCancel,
    "large",
  );
};
